import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Table, Button, Modal, Row, Col } from 'react-bootstrap';
import REPORTS_CATALOG from '../data/reportsCatalog';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { useAuth } from '../context/AuthContext';
import { todayDR, firstOfMonthDR } from '../utils/dates';

// Reports that expose expenses, cost, profit, rankings or company-wide KPIs: the server refuses them to non-admins.
const ADMIN_ONLY_REPORTS = new Set([
  'expenses-summary', 'expenses-by-category', 'product-margin', 'profit-and-loss', // expenses, cost, profit
  'sales-detail', 'sales-by-customer', 'sales-by-product', 'new-customers', 'inactive-customers', // company sales, rankings / customer analytics
  'collections', 'dso', 'repeat-client-rate', 'revenue-concentration', 'reorder-cycle', 'itbis-payable', 'avg-invoice-value', // company KPIs
]);

const FAVORITES_KEY = 'ventas_favorite_reports';

const ALL_REPORTS = REPORTS_CATALOG.flatMap(group =>
  group.reports.map(r => ({ ...r, category: group.category }))
);

const firstOfMonth = firstOfMonthDR;
const today = todayDR; // the Dominican day (toISOString is UTC: tomorrow after 8 p.m.)

const ReportCatalog = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const catalog = useMemo(
    () => (isAdmin ? REPORTS_CATALOG : REPORTS_CATALOG
      .map(group => ({ ...group, reports: group.reports.filter(r => !ADMIN_ONLY_REPORTS.has(r.id)) }))
      .filter(group => group.reports.length > 0)),
    [isAdmin],
  );
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [search, setSearch] = useState('');
  const [modalReport, setModalReport] = useState(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('E31');

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error('Error loading clients:', err));
  }, []);

  const openModal = (report) => {
    setSelectedClient('');
    setStatusFilter('all');
    setModalReport(report);
  };

  const handleRun = () => {
    const params = new URLSearchParams({ id: modalReport.id });
    if (modalReport.dateRange) {
      params.set('from', from);
      params.set('to', to);
    }
    if ((modalReport.customerSpecific || modalReport.clientOptional) && selectedClient) {
      params.set('client', selectedClient);
    }
    if (modalReport.statusFilter && statusFilter !== 'all') {
      params.set('status', statusFilter);
    }
    if (modalReport.tipoFilter && tipoFilter !== 'all') {
      params.set('tipo', tipoFilter);
    }
    setModalReport(null);
    window.open(`/reports/print?${params.toString()}`, '_blank');
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  const favoriteReports = useMemo(
    () => ALL_REPORTS.filter(r => favorites.includes(r.id) && (isAdmin || !ADMIN_ONLY_REPORTS.has(r.id))),
    [favorites, isAdmin]
  );

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;

    return catalog
      .map(group => ({
        category: group.category,
        reports: group.category.toLowerCase().includes(term)
          ? group.reports
          : group.reports.filter(r => r.name.toLowerCase().includes(term)),
      }))
      .filter(group => group.reports.length > 0);
  }, [search, catalog]);

  const StarButton = ({ id }) => (
    <button
      type="button"
      onClick={() => toggleFavorite(id)}
      title={favorites.includes(id) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '1.1rem' }}
    >
      <i
        className={favorites.includes(id) ? 'bi bi-star-fill' : 'bi bi-star'}
        style={{ color: favorites.includes(id) ? 'var(--mm-yellow)' : 'var(--mm-muted)' }}
      />
    </button>
  );

  const RunButton = ({ report }) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      title="Ejecutar Reporte"
      onClick={() => openModal(report)}
    >
      <i className="bi bi-play-fill" />
    </button>
  );

  return (
    <div className="mt-3">
      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Favoritos</Card.Title>
        {favoriteReports.length === 0 ? (
          <div className="text-muted">
            No tienes reportes favoritos todavía. Marca la estrella junto a cualquier reporte en la tabla para agregarlo aquí.
          </div>
        ) : (
          <Table hover size="sm" className="mb-0">
            <tbody>
              {favoriteReports.map(r => (
                <tr key={r.id}>
                  <td style={{ width: 32 }}><StarButton id={r.id} /></td>
                  <td>{r.name}</td>
                  <td className="text-muted">{r.category}</td>
                  <td className="text-end">
                    <RunButton report={r} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="shadow-sm p-3">
        <Row className="mb-3">
          <Col md={5}>
            <Form.Control
              placeholder="Buscar reporte o categoría..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Col>
        </Row>

        <Table hover responsive size="sm" className="mb-0">
          <thead>
            <tr><th style={{ width: 32 }}></th><th>Reporte</th><th>Categoría</th><th className="text-end">Acción</th></tr>
          </thead>
          <tbody>
            {filteredGroups.map(group => (
              <React.Fragment key={group.category}>
                <tr>
                  <td colSpan={4} style={{ background: 'var(--mm-panel-2)', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>
                    {group.category}
                  </td>
                </tr>
                {group.reports.map(r => (
                  <tr key={r.id}>
                    <td><StarButton id={r.id} /></td>
                    <td>{r.name}</td>
                    <td className="text-muted">{group.category}</td>
                    <td className="text-end">
                      <RunButton report={{ ...r, category: group.category }} />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {filteredGroups.length === 0 && (
              <tr><td colSpan={4} className="text-muted text-center">Ningún reporte coincide con "{search}".</td></tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Modal show={!!modalReport} onHide={() => setModalReport(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalReport?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(modalReport?.customerSpecific || modalReport?.clientOptional || modalReport?.statusFilter || modalReport?.tipoFilter || modalReport?.dateRange) ? (
            <>
              {(modalReport?.customerSpecific || modalReport?.clientOptional) && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Cliente
                    {modalReport?.clientOptional && <span className="text-muted"> (opcional — déjalo en blanco para todos)</span>}
                  </Form.Label>
                  <div className="d-flex gap-2 align-items-start">
                    <div className="flex-grow-1">
                      <SearchSelect
                        options={clients}
                        value={selectedClient}
                        getKey={c => c._id}
                        getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
                        onSelect={c => setSelectedClient(c._id)}
                        placeholder="Buscar cliente..."
                        emptyLabel="Sin clientes coincidentes"
                      />
                    </div>
                    {modalReport?.clientOptional && selectedClient && (
                      <Button size="sm" variant="outline-secondary" onClick={() => setSelectedClient('')}>Todos</Button>
                    )}
                  </div>
                </Form.Group>
              )}
              {modalReport?.statusFilter && (
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">Todos</option>
                    <option value="delivered">Entregados</option>
                  </Form.Select>
                </Form.Group>
              )}
              {modalReport?.tipoFilter && (
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de comprobante</Form.Label>
                  <Form.Select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}>
                    <option value="all">Todos</option>
                    <option value="E31">Factura de Crédito Fiscal (E31)</option>
                    <option value="E32">Factura de Consumo (E32)</option>
                    <option value="E33">Nota de Débito (E33)</option>
                    <option value="E34">Nota de Crédito (E34)</option>
                  </Form.Select>
                </Form.Group>
              )}
              {modalReport?.dateRange && (
                <>
                  <p className="text-muted">Selecciona el rango de fechas para este reporte.</p>
                  <Row>
                    <Col>
                      <Form.Label>Desde</Form.Label>
                      <Form.Control type="date" value={from} onChange={e => setFrom(e.target.value)} />
                    </Col>
                    <Col>
                      <Form.Label>Hasta</Form.Label>
                      <Form.Control type="date" value={to} onChange={e => setTo(e.target.value)} />
                    </Col>
                  </Row>
                </>
              )}
            </>
          ) : (
            <p className="mb-0">¿Deseas ejecutar el reporte <strong>{modalReport?.name}</strong>?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setModalReport(null)}>Cancelar</Button>
          <Button
            onClick={handleRun}
            disabled={modalReport?.customerSpecific && !selectedClient}
          >
            Ejecutar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportCatalog;
